import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { addPartnership, createOrg, removePartnership } from '../../api/endpoints';
import { qk, useAdminOrgs } from '../../api/hooks';
import type { AdminOrg, OrgType } from '../../api/types';
import { toast } from '../../app/toast';
import { Seg } from '../../components/Seg';
import { ErrorState, Loading } from '../../components/States';

export function OrgsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const orgs = useAdminOrgs();
  const [form, setForm] = useState(false);

  const toggle = useMutation({
    mutationFn: ({ clinic, lab, on }: { clinic: AdminOrg; lab: AdminOrg; on: boolean }) =>
      on ? removePartnership(clinic.id, lab.id) : addPartnership(clinic.id, lab.id),
    onSuccess: (_r, { on }) => {
      toast(on ? t('admin.orgs.partnerOff') : t('admin.orgs.partnerOn'));
      void qc.invalidateQueries({ queryKey: qk.orgs });
      void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
  });

  if (orgs.isLoading) return <Loading />;
  if (!orgs.data) return <ErrorState error={orgs.error} onRetry={() => void orgs.refetch()} />;
  const clinics = orgs.data.filter((o) => o.type === 'clinic');
  const labs = orgs.data.filter((o) => o.type === 'lab');
  const isPartner = (c: AdminOrg, l: AdminOrg) =>
    c.partner_ids.includes(l.id) || l.partner_ids.includes(c.id);
  const names = (o: AdminOrg) =>
    o.members.length ? (
      o.members.map((m, i) => (
        <span key={m.id}>
          {i > 0 && <br />}
          {m.name}
        </span>
      ))
    ) : (
      <span className="muted">{t('admin.orgs.noMembers')}</span>
    );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('admin.orgs.title')}</h1>
          <p>{t('admin.orgs.sub')}</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setForm(true)}>
          {t('admin.orgs.add')}
        </button>
      </div>
      {form && <OrgForm onClose={() => setForm(false)} />}

      <h2>{t('admin.orgs.clinics')}</h2>
      <div className="table-wrap plain">
        <table>
          <thead>
            <tr>
              <th>{t('admin.orgs.cols.clinic')}</th>
              <th>{t('admin.orgs.cols.doctors')}</th>
              <th>{t('admin.orgs.cols.active')}</th>
              <th>{t('admin.orgs.cols.partners')}</th>
            </tr>
          </thead>
          <tbody>
            {clinics.map((o) => (
              <tr key={o.id}>
                <td className="pname">
                  {o.name}
                  {o.status === 'blocked' && (
                    <>
                      {' '}
                      <span className="tag">{t('admin.orgs.blocked')}</span>
                    </>
                  )}
                </td>
                <td>{names(o)}</td>
                <td>{o.active_cases}</td>
                <td>
                  <div className="chips">
                    {labs.map((l) => {
                      const on = isPartner(o, l);
                      return (
                        <button
                          key={l.id}
                          type="button"
                          className="chip"
                          aria-pressed={on}
                          disabled={toggle.isPending}
                          onClick={() => toggle.mutate({ clinic: o, lab: l, on })}
                        >
                          {l.name}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small" style={{ margin: '8px 0 26px' }}>
        {t('admin.orgs.partnerHint')}
      </p>

      <h2>{t('admin.orgs.labs')}</h2>
      <div className="table-wrap plain">
        <table>
          <thead>
            <tr>
              <th>{t('admin.orgs.cols.lab')}</th>
              <th>{t('admin.orgs.cols.techs')}</th>
              <th>{t('admin.orgs.cols.active')}</th>
              <th>{t('admin.orgs.cols.partnerCount')}</th>
            </tr>
          </thead>
          <tbody>
            {labs.map((o) => (
              <tr key={o.id}>
                <td className="pname">
                  {o.name}
                  {o.status === 'blocked' && (
                    <>
                      {' '}
                      <span className="tag">{t('admin.orgs.blocked')}</span>
                    </>
                  )}
                </td>
                <td>{names(o)}</td>
                <td>{o.active_cases}</td>
                <td>{clinics.filter((c) => isPartner(c, o)).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function OrgForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [type, setType] = useState<OrgType>('clinic');
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    meta: { manualError: true },
    mutationFn: () => createOrg({ type, name: name.trim() }),
    onSuccess: (o) => {
      toast(t(o.type === 'lab' ? 'admin.orgs.createdLab' : 'admin.orgs.createdClinic', { name: o.name }));
      void qc.invalidateQueries({ queryKey: qk.orgs });
      void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.userMessage : t('errors.unknown')),
  });
  return (
    <section className="wiz" style={{ maxWidth: 'none', marginBottom: 22 }}>
      <h2>{t('admin.orgs.newTitle')}</h2>
      <Seg<OrgType>
        label={t('admin.orgs.type')}
        options={[
          ['clinic', t('admin.orgs.typeClinic')],
          ['lab', t('admin.orgs.typeLab')],
        ]}
        value={type}
        onChange={setType}
      />
      <label className="field" style={{ marginTop: 14, maxWidth: 420 }}>
        {t('admin.orgs.name')}
        <input
          className="input"
          value={name}
          autoComplete="off"
          onChange={(e) => {
            setName(e.target.value);
            setErr(null);
          }}
        />
      </label>
      {err && (
        <div className="err" role="alert">
          {err}
        </div>
      )}
      <div className="wiz-foot">
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={create.isPending}
          onClick={() => (name.trim() ? create.mutate() : setErr(t('admin.orgs.nameRequired')))}
        >
          {t('admin.orgs.create')}
        </button>
      </div>
    </section>
  );
}
